import React, { useRef, useState } from "react";
import api, { resolveMediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Extracts a usable image URL from user input.
 * - Direct URL → returned as-is.
 * - HTML embed code (e.g. from imgbb, Postimage) → pulls the first <img src="…"> or href="…jpg".
 * - Data URL → passed through.
 */
function extractImageUrl(input) {
  if (!input) return "";
  const trimmed = input.trim();
  // Already a plain URL or data URL — no HTML tags
  if (!/</.test(trimmed)) return trimmed;
  // Try to pull an <img src="...">
  const imgMatch = trimmed.match(/<img[^>]+src=["']([^"']+)["']/i);
  if (imgMatch) return imgMatch[1];
  // Fallback: pull the first href that ends in an image extension
  const hrefMatch = trimmed.match(/href=["']([^"']+\.(?:jpe?g|png|gif|webp))["']/i);
  if (hrefMatch) return hrefMatch[1];
  return trimmed;
}

/**
 * Cover image picker: URL input + file upload, with live preview.
 */
export default function CoverImagePicker({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const handleUrlChange = (raw) => {
    const clean = extractImageUrl(raw);
    if (clean !== raw && clean) {
      toast.info("Extracted image URL from embed code");
    }
    onChange(clean);
    setPreviewError(false);
  };

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
      toast.error("Only JPG, PNG, WEBP or GIF images are allowed");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8 MB — please compress it first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      setPreviewError(false);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clear = () => { onChange(""); setPreviewError(false); };
  const resolved = resolveMediaUrl(value);
  const showPreview = !!resolved;

  return (
    <div className="space-y-2">
      <Label className="text-xs font-mono-fin uppercase text-slate-500">Cover Image</Label>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          value={value || ""}
          onChange={e => handleUrlChange(e.target.value)}
          onPaste={e => {
            const pasted = e.clipboardData.getData("text");
            if (/</.test(pasted)) {
              e.preventDefault();
              handleUrlChange(pasted);
            }
          }}
          placeholder="Paste image URL or embed code — or upload from device →"
          className="bg-slate-50 border-slate-200 font-mono-fin flex-1"
          data-testid="blog-cover-url-input"
        />
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={upload} className="hidden" data-testid="blog-cover-file-input" />
        <Button type="button" variant="outline" onClick={pick} disabled={uploading} className="border-slate-200 shrink-0" data-testid="blog-cover-upload-btn">
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>

      {showPreview && (
        <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-video max-w-md" data-testid="blog-cover-preview">
          {!previewError ? (
            <img
              src={resolved}
              alt="Cover preview"
              onError={() => setPreviewError(true)}
              onLoad={() => setPreviewError(false)}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center px-4 text-slate-500">
              <ImageIcon className="w-8 h-8 mb-2" />
              <div className="text-xs font-mono-fin uppercase">Image failed to load</div>
              <div className="text-[10px] mt-1 text-slate-400 break-all">Check the URL or upload a file instead</div>
            </div>
          )}
          <button type="button" onClick={clear} className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-sm border border-slate-200" data-testid="blog-cover-clear">
            <X className="w-3.5 h-3.5 text-slate-700" />
          </button>
        </div>
      )}
    </div>
  );
}
